"use client";
import { PlaylistPreset } from "@/components/video/video-card/components/playlist-preset";
import { PlaylistTreatedAsVideoPreset } from "@/components/video/video-card/components/playlist-treated-as-video-preset";
import { SuperLink as Link } from "@/components/common/super-link";
import { PriceLink } from "@/components/ecommerce/price-link";
import {
  createPlaylistEpisodePath,
  createPlaylistPath,
} from "@/components/video/video-card/service";
import { cn } from "@/lib/utils";
import { PictureUpload } from "@/components/common/picture-upload/picture-upload";
import { resolvePluralsEpisodes } from "@/service/text/plurals";
import { verboseDatetimeFormat } from "@/service/date/article";
import { PlaylistEpisodePreset } from "./components/playlist-episode-preset";
import { ImageHoverWrapper } from "@/components/common/image-hover-wrapper";
import { PlayIcon } from "lucide-react";

export const VideoCard = ({
  data,
  episodeNumber,
  active = false,
  instantImageLoad = false,
  onClick = () => {},
  aside = false,
}: {
  data: VideoPreviewType;
  episodeNumber?: number;
  active?: boolean;
  instantImageLoad?: boolean;
  onClick?: () => void;
  aside?: boolean;
}) => {
  const { viewModelType, image, title } = data;
  const link =
    viewModelType === "playlistEpisodePreview"
      ? createPlaylistEpisodePath(data as PlaylistEpisodePreviewVM)
      : createPlaylistPath(
          data as PlaylistPreviewVM | PlaylistTreatedAsVideoPreviewVM,
        );

  return (
    <div id={episodeNumber != undefined ? `part-${episodeNumber}` : undefined}>
      <ImageHoverWrapper
        className={cn("relative mb-4", aside && "xl:mb-2")}
        rounded={cn("rounded-xl", aside && "xl:rounded-lg")}
      >
        <Link
          href={link}
          {...(episodeNumber ? { "data-part-id": episodeNumber } : {})}
          onClick={onClick}
        >
          <PictureUpload
            image={image}
            loaded={instantImageLoad}
            aspectRatio={"1.91 / 1"}
            type={"card"}
          />

          {viewModelType === "playlistEpisodePreview" && (
            <PlaylistEpisodePreset
              active={active}
              data={data as PlaylistEpisodePreviewVM}
            />
          )}

          {viewModelType === "playlistPreview" && (
            <PlaylistPreset data={data as PlaylistPreviewVM} />
          )}

          {viewModelType === "playlistTreatedAsVideoPreview" && (
            <PlaylistTreatedAsVideoPreset
              data={data as PlaylistTreatedAsVideoPreviewVM}
            />
          )}
          <div className="pointer-events-none absolute top-0 left-0 z-20 flex h-full w-full cursor-pointer items-center justify-center opacity-0 transition group-hover:opacity-100">
            <div className="flex h-10 w-10 scale-75 transform items-center justify-center rounded-lg bg-white backdrop-blur-2xl transition-transform duration-200 group-hover:scale-100">
              <PlayIcon size={16} />
            </div>
          </div>
        </Link>
      </ImageHoverWrapper>

      <div
        className={cn(
          "mb-1.5 text-base font-[550] text-zinc-800",
          aside && "xl:text-sm",
        )}
      >
        <Link
          href={link}
          onClick={onClick}
          className={"text-remedium-blue-hover"}
        >
          {title}
        </Link>
      </div>

      <div
        className={`text-muted-foreground inline-flex min-h-6 flex-wrap items-center gap-1.5 text-sm empty:hidden`}
      >
        {viewModelType === "playlistPreview" && (
          <PlaylistBottom playlist={data as PlaylistPreviewVM} link={link} />
        )}
        {viewModelType === "playlistEpisodePreview" && !aside && (
          <PlaylistEpisodeBottom
            episode={data as PlaylistEpisodePreviewVM}
            aside={aside}
          />
        )}
        {viewModelType === "playlistTreatedAsVideoPreview" && !aside && (
          <PlaylistTreatedAsVideoBottom
            link={link}
            playlist={data as PlaylistTreatedAsVideoPreviewVM}
          />
        )}
      </div>
    </div>
  );
};

const PlaylistBottom = ({
  playlist: { playlistLength, product },
  link,
}: {
  playlist: PlaylistPreviewVM;
  link: string;
}) => (
  <>
    {product && <PriceLink product={product} link={link} />}
    <div className={"font-semibold"}>Playlista</div>
    <div>·</div>
    <div>{resolvePluralsEpisodes(playlistLength as number)}</div>
  </>
);

const PlaylistEpisodeBottom = ({
  episode: {
    playlist: { title, isSeries },
    publishedAt,
    playlistGroup,
  },
  aside,
}: {
  episode: PlaylistEpisodePreviewVM;
  aside: boolean;
}) => (
  <>
    {!aside && (
      <>
        <div className={"font-semibold"}>{playlistGroup?.title ?? title}</div>
        {isSeries && <div>·</div>}
      </>
    )}

    <div>{verboseDatetimeFormat(publishedAt as string)}</div>
  </>
);

const PlaylistTreatedAsVideoBottom = ({
  playlist: { publishedAt, product },
  link,
}: {
  playlist: PlaylistTreatedAsVideoPreviewVM;
  link: string;
}) => (
  <>
    {product && <PriceLink product={product} link={link} />}
    <div>{verboseDatetimeFormat(publishedAt as string)}</div>
  </>
);
